import router from '@/router'
import store from '@/store'
// eslint-disable-next-line object-curly-newline
import { reactive, getCurrentInstance, watch, toRefs } from '@vue/composition-api'

export const isObject = obj => typeof obj === 'object' && obj !== null

export const isToday = date => {
  const today = new Date()
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  )
}

// CURRENCY FORMATTER
let formatter = null;

const userLocale = navigator.language || navigator.userLanguage;

// Determine the decimal separator based on the locale

function parseNumber(value) {
  if (typeof value === 'number') return value;

  if (typeof value !== 'string') {
    value = String(value);
  }

  function parsePotentiallyGroupedFloat(stringValue) {
    stringValue = stringValue.trim();
    var result = stringValue.replace(/[^0-9]/g, '');
    if (/[,\.]\d{2}$/.test(stringValue)) {
        result = result.replace(/(\d{2})$/, '.$1');
    }
    return parseFloat(result);
  }
  // console.log('parsePotentiallyGroupedFloat',parsePotentiallyGroupedFloat(value))

  // Remove any non-numeric characters except the decimal point

  return parsePotentiallyGroupedFloat(value);;
}

let currencySymbol = '';
const initFormatter = (locale = userLocale) => {
  // Get currency code and locale map from the store
  // const currencyCode = store.getters['adinSettings/getCurrencyCode'];
  // const currencyLocaleMap = store.getters['adinSettings/getCurrencyLocaleMap'];
  currencySymbol = store.getters['adinSettings/getCurrencySymbol'];
  // Find the suitable locale based on the currency code, if not found, use user locale
  // const suitableLocale = currencyLocaleMap[currencyCode] || locale;

  // All customers' default locale is en-US
  const fixedLocale = 'en-US';
  const options = { maximumFractionDigits: 4, };
  formatter = new Intl.NumberFormat(fixedLocale, options);
};



export const universalFormatter = (number, config = {}) => {
  const { decimals = null, maxDecimals = 4, outputType = null, isCurrency = false, isPercentage = false, kFormat = null, debug = false } = config;
  // outputType: If set to 'parts', returns the formatted number as separate components.
  // decimals: Specifies the number of decimal places. If null, it determines automatically based on the value.
  // maxDecimals: Specifies the maximum number of decimal places. Unlike decimals, it removes trailing zeroes if possible.
  // isCurrency: When true, formats the number as currency; otherwise, uses standard formatting.
  // kFormat: When true, abbreviates large numbers (e.g., 1,000 → 1k).

  // If the formatter is not initialized, initialize it
  if (!formatter) {
    initFormatter();
  }

  // ! what should be return if the value is undefined?
  // falsy but not 0
  // console.log('number',number)
  if((!number && number !== 0)|| (number === '-')) {
    // console.log('formatted to falsy value', formatter.formatToParts(0));
    let falsyOutput;
    if(outputType === 'parts') {
      falsyOutput = {
        integerPart: '-',
        fractionalPart: null,
      };
    }else{
      falsyOutput = '-';
    }
    return falsyOutput
  };

  try {
    // console.log('number',number)
    const convertedNumber = parseNumber(number);

    if (kFormat) {
      const absNumber = Math.abs(convertedNumber);
      let formatted;
      let suffix = '';

      if (absNumber >= 1e9) {
        formatted = convertedNumber / 1e9;
        suffix = 'B';
      } else if (absNumber >= 1e6) {
        formatted = convertedNumber / 1e6;
        suffix = 'M';
      } else if (absNumber >= 1e3) {
        formatted = convertedNumber / 1e3;
        suffix = 'K';
      } else {
        formatted = convertedNumber;
      }


      // Format with suffix
      const baseFormatted = universalFormatter(
          formatted,
          {
            ...config,
            kFormat: false,
            maxDecimals: 1
          }
      );

      if (outputType === 'parts') {
        return {
          integerPart: baseFormatted.integerPart,
          fractionalPart: baseFormatted.fractionalPart,
          suffix
        };
      }
      return baseFormatted + suffix;
    }

    let decimalPlaces;
    if (decimals !== null) {
        decimalPlaces = parseNumber(decimals);
    } else {
      const absNumber = Math.abs(convertedNumber);
      if (absNumber % 1 > 0){
        // Remove trailing zeros and get actual decimals
        const strNum = absNumber.toString();
        const decimalPos = strNum.indexOf(".");
        const actualDecimals = decimalPos === -1 ? 0 : strNum.length - decimalPos - 1;
        // Determine decimals based on number magnitude
        const magnitudeDecimals = Math.max(0, 2 - Math.floor(Math.log10(absNumber)));
        // Take the minimum
        decimalPlaces = Math.min(actualDecimals, maxDecimals, magnitudeDecimals);
      }else{
        decimalPlaces = 0
      }
    }

    // console.log('value',convertedNumber, 'decimals',decimalPlaces)

    // Apply decimalPlaces by fixing the number string first
    const fixedNumber = convertedNumber.toFixed(decimalPlaces);
    const parts = formatter.formatToParts(Number(fixedNumber));


    // Reconstruct the formatted string with our specific decimal places
    let integerPart = '';
    let fractionalPart = '';
    let seenDecimal = false;

    for (const part of parts) {
      // console.log( 'integerPart:',integerPart,'frac:',fractionalPart, 'part:',part)
      if (part.type === 'decimal') {
        if (decimalPlaces > 0) {
          fractionalPart += part.value;
        }
        seenDecimal = true;
      } else if (part.type === 'fraction') {
        if (decimalPlaces > 0) {
          fractionalPart += part.value;
        }
      } else if (!seenDecimal || part.type !== 'fraction') {
        if (part.type !== 'currency') {
          integerPart += part.value;
        }
      }
    }
    // Remove trailing zeros
    fractionalPart = fractionalPart.replace(/0+$/, '').replace(/[.,]$/, '');

    if (isNaN(integerPart.replace(/[.,]/g, ''))) {
      // If either part is NaN, return integerPart as '-' and fractionalPart as an empty string
      integerPart = '-';
      fractionalPart = '';
    }

    if(isCurrency) {
      integerPart = currencySymbol + integerPart;
    }

    let output;

    if(outputType === 'parts') {
      output = {
        integerPart,
        fractionalPart
      }
    }else{
      if(isPercentage) {
        output = `${integerPart}${fractionalPart}%`
      } else {
        output = `${integerPart}${fractionalPart}`

      }
    }
    // console.log('value',convertedNumber)
    // console.log('output',output)
    return output;
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${number} ${store.getters['adinSettings/getCurrencyCode']}`;
  }
};



const getRandomFromArray = array => array[Math.floor(Math.random() * array.length)]

// ? Light and Dark variant is not included
// prettier-ignore
export const getRandomBsVariant = () => getRandomFromArray(['primary', 'secondary', 'success', 'warning', 'danger', 'info'])

export const isDynamicRouteActive = route => {
  const { route: resolvedRoute } = router.resolve(route)
  return resolvedRoute.path === router.currentRoute.path
}

// Thanks: https://medium.com/better-programming/reactive-vue-routes-with-the-composition-api-18c1abd878d1
export const useRouter = () => {
  const vm = getCurrentInstance().proxy
  const state = reactive({
    route: vm.$route,
  })

  watch(
    () => vm.$route,
    r => {
      state.route = r
    },
  )

  return { ...toRefs(state), router: vm.$router }
}

import * as XLSX from 'xlsx-js-style';

export const exportDataToXLSX = (dataArray, filename) => {
  const flattenedData = [];
  const mainRowIndices = [];
  
  // Helper function to determine if a property should be excluded
  const shouldExcludeProperty = (prop) => {
    // Convert to lowercase for case-insensitive comparison
    const lowerProp = prop.toLowerCase();
    
    // Remove boostrap _showDetails for subrow structure
    if (lowerProp.includes('showdetails')) return true;
    
    // Check for various id patterns (admind_id, admindId or admindID, a key ending with _id, Id or ID)
    if (/^id$|[a-zA-Z0-9]+(_id|Id|ID)$/.test(prop)) return true;
    
    // Internal properties we always want to exclude
    const internalProps = ['subrows', 'ismainrow'];
    if (internalProps.includes(lowerProp)) return true;
    
    return false;
  };
  
  // Clean an object by removing unwanted properties
  const cleanObject = (obj) => {
    const cleanedObj = {};
    
    Object.keys(obj).forEach(key => {
      if (!shouldExcludeProperty(key)) {
        cleanedObj[key] = obj[key];
      }
    });
    
    return cleanedObj;
  };
  
  // Process each main row and its subrows
  dataArray.forEach(data => {
    const rowIndex = flattenedData.length;
    
    // Add the main row first (cleaned)
    const mainRow = cleanObject(data);
    // No need for explicit deletion as cleanObject already filters these out
    flattenedData.push(mainRow);
    mainRowIndices.push(rowIndex + 1); // +1 for header row
    
    // Add all subrows (cleaned)
    if (data.subRows && data.subRows.length > 0) {
      data.subRows.forEach(subRow => {
        const cleanSubRow = cleanObject(subRow);
        flattenedData.push(cleanSubRow);
      });
    }
  });
  
  // Create a worksheet
  const worksheet = XLSX.utils.json_to_sheet(flattenedData);

  // Format headers
  const headerRow = Object.keys(flattenedData[0] || {});
  const formattedHeaders = headerRow.map(header =>
    header
      .replace(/([a-z])([A-Z])/g, "$1 $2") // Convert camelCase to space-separated
      .replace(/_/g, " ") // Convert snake_case to space-separated
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
  );
  
  // Replace headers in worksheet
  formattedHeaders.forEach((header, index) => {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
    worksheet[cellRef] = {
      v: header,
      t: "s",
      s: {
        font: { bold: true }, // Make bold
        alignment: { horizontal: "center" } // Center align
      }
    };
  });

  const range = XLSX.utils.decode_range(worksheet['!ref']);
  
  // Set up row outlines
  worksheet['!rows'] = [];
  flattenedData.forEach((_, index) => {
    worksheet['!rows'][index + 1] = {
      level: mainRowIndices.includes(index + 1) ? 0 : 1,
      hidden: false
    };
  });
  
  worksheet['!outline'] = { above: false };
  
  // Define styles for main rows
  const mainRowStyle = {
    font: {
      bold: true,
      color: { rgb: "000000" } // Black text
    },
    fill: {
      patternType: "solid",
      fgColor: { rgb: "E6F2FF" } // Light blue background
    }
  };
  
  // Apply styles to all cells in main rows
  mainRowIndices.forEach(rowIndex => {
    for (let col = 0; col <= range.e.c; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: col });
      
      if (worksheet[cellRef]) {
        // Save the existing value and type
        const value = worksheet[cellRef].v;
        const type = worksheet[cellRef].t;
        
        // Apply style while preserving value and type
        worksheet[cellRef] = {
          v: value,
          t: type,
          s: mainRowStyle
        };
      }
    }
  });
  
  // Create workbook and add worksheet
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Campaign Data");
  
  // Generate and download
  const excelBuffer = XLSX.write(workbook, { 
    bookType: 'xlsx', 
    type: 'array',
    compression: true
  });
  
  const blob = new Blob([excelBuffer], { 
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
  });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename + ".xlsx");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * This is just enhancement over Object.extend [Gives deep extend]
 * @param {target} a Object which contains values to be overridden
 * @param {source} b Object which contains values to override
 */
// export const objectExtend = (a, b) => {
//   // Don't touch 'null' or 'undefined' objects.
//   if (a == null || b == null) {
//     return a
//   }

//   Object.keys(b).forEach(key => {
//     if (Object.prototype.toString.call(b[key]) === '[object Object]') {
//       if (Object.prototype.toString.call(a[key]) !== '[object Object]') {
//         // eslint-disable-next-line no-param-reassign
//         a[key] = b[key]
//       } else {
//         // eslint-disable-next-line no-param-reassign
//         a[key] = objectExtend(a[key], b[key])
//       }
//     } else {
//       // eslint-disable-next-line no-param-reassign
//       a[key] = b[key]
//     }
//   })

//   return a
// }
